import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'

export const Route = createFileRoute('/_layout/settings')({
  component: SettingsComponent,
})

interface Settings {
  // API Keys
  polymarketApiKey: string
  kalshiApiKey: string
  predictitApiKey: string
  
  // Trading Settings
  enableTrading: boolean
  maxPositionSize: number
  maxOpenPositions: number
  minSpreadThreshold: number
  
  // Notification Settings
  emailAddress: string
  phoneNumber: string
  emailEnabled: boolean
  smsEnabled: boolean
  
  // Display Settings
  theme: string
  refreshInterval: number
  showTestMarkets: boolean
  
  // Risk Management
  stopLossPercent: number
  takeProfitPercent: number
  maxDailyLoss: number
}

function SettingsComponent() {
  const [settings, setSettings] = useState<Settings>({
    // API Keys
    polymarketApiKey: '',
    kalshiApiKey: '',
    predictitApiKey: '',
    
    // Trading Settings
    enableTrading: false,
    maxPositionSize: 1000,
    maxOpenPositions: 10,
    minSpreadThreshold: 5,
    
    // Notification Settings
    emailAddress: '',
    phoneNumber: '',
    emailEnabled: true,
    smsEnabled: false,
    
    // Display Settings
    theme: 'dark',
    refreshInterval: 30,
    showTestMarkets: false,
    
    // Risk Management
    stopLossPercent: 5,
    takeProfitPercent: 10,
    maxDailyLoss: 1000
  })
  
  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const response = await fetch('https://skybit-api.botnet-599.workers.dev/settings')
      if (response.ok) {
        const data = await response.json()
        setSettings(prev => ({ ...prev, ...data }))
      }
    } catch (err) {
      console.error('Failed to load settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    setSaveStatus('Saving...')
    try {
      const response = await fetch('https://skybit-api.botnet-599.workers.dev/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      })
      
      if (!response.ok) throw new Error('Failed to save settings')
      
      setSaveStatus('Settings saved successfully!')
      setTimeout(() => setSaveStatus(''), 3000)
    } catch (err) {
      setSaveStatus('Failed to save settings')
      setTimeout(() => setSaveStatus(''), 3000)
    }
  }

  const handleInputChange = (section: string, field: keyof Settings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading settings...</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '800px' }}>
      <h2 style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>SETTINGS</h2>

      {/* API Keys Section */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
          API KEYS
        </h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              POLYMARKET API KEY
            </label>
            <input
              type="password"
              value={settings.polymarketApiKey}
              onChange={(e) => handleInputChange('api', 'polymarketApiKey', e.target.value)}
              placeholder="Enter your Polymarket API key"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem',
                fontFamily: 'monospace'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              KALSHI API KEY
            </label>
            <input
              type="password"
              value={settings.kalshiApiKey}
              onChange={(e) => handleInputChange('api', 'kalshiApiKey', e.target.value)}
              placeholder="Enter your Kalshi API key"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem',
                fontFamily: 'monospace'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              PREDICTIT API KEY
            </label>
            <input
              type="password"
              value={settings.predictitApiKey}
              onChange={(e) => handleInputChange('api', 'predictitApiKey', e.target.value)}
              placeholder="Enter your PredictIt API key"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem',
                fontFamily: 'monospace'
              }}
            />
          </div>
        </div>
      </div>

      {/* Trading Settings */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
          TRADING SETTINGS
        </h3>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.enableTrading}
                onChange={(e) => handleInputChange('trading', 'enableTrading', e.target.checked)}
              />
              ENABLE LIVE TRADING
            </label>
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              MAX POSITION SIZE ($)
            </label>
            <input
              type="number"
              value={settings.maxPositionSize}
              onChange={(e) => handleInputChange('trading', 'maxPositionSize', parseInt(e.target.value))}
              min="0"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              MAX OPEN POSITIONS
            </label>
            <input
              type="number"
              value={settings.maxOpenPositions}
              onChange={(e) => handleInputChange('trading', 'maxOpenPositions', parseInt(e.target.value))}
              min="1"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              MIN SPREAD THRESHOLD (%)
            </label>
            <input
              type="number"
              value={settings.minSpreadThreshold}
              onChange={(e) => handleInputChange('trading', 'minSpreadThreshold', parseFloat(e.target.value))}
              min="0"
              step="0.5"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
          NOTIFICATIONS
        </h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              EMAIL ADDRESS
            </label>
            <input
              type="email"
              value={settings.emailAddress}
              onChange={(e) => handleInputChange('notifications', 'emailAddress', e.target.value)}
              placeholder="your@email.com"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              PHONE NUMBER (SMS)
            </label>
            <input
              type="tel"
              value={settings.phoneNumber}
              onChange={(e) => handleInputChange('notifications', 'phoneNumber', e.target.value)}
              placeholder="+1234567890"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '2rem' }}>
            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.emailEnabled}
                onChange={(e) => handleInputChange('notifications', 'emailEnabled', e.target.checked)}
              />
              EMAIL ALERTS
            </label>
            
            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.smsEnabled}
                onChange={(e) => handleInputChange('notifications', 'smsEnabled', e.target.checked)}
              />
              SMS ALERTS
            </label>
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
          DISPLAY
        </h3>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              THEME
            </label>
            <select
              value={settings.theme}
              onChange={(e) => handleInputChange('display', 'theme', e.target.value)}
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
              <option value="auto">Auto</option>
            </select>
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              REFRESH INTERVAL (seconds)
            </label>
            <input
              type="number"
              value={settings.refreshInterval}
              onChange={(e) => handleInputChange('display', 'refreshInterval', parseInt(e.target.value))}
              min="5"
              max="300"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.showTestMarkets}
                onChange={(e) => handleInputChange('display', 'showTestMarkets', e.target.checked)}
              />
              SHOW TEST MARKETS
            </label>
          </div>
        </div>
      </div>

      {/* Risk Management */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
          RISK MANAGEMENT
        </h3>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              STOP LOSS (%)
            </label>
            <input
              type="number"
              value={settings.stopLossPercent}
              onChange={(e) => handleInputChange('risk', 'stopLossPercent', parseFloat(e.target.value))}
              min="0"
              max="100"
              step="0.5"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              TAKE PROFIT (%)
            </label>
            <input
              type="number"
              value={settings.takeProfitPercent}
              onChange={(e) => handleInputChange('risk', 'takeProfitPercent', parseFloat(e.target.value))}
              min="0"
              max="100"
              step="0.5"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
              MAX DAILY LOSS ($)
            </label>
            <input
              type="number"
              value={settings.maxDailyLoss}
              onChange={(e) => handleInputChange('risk', 'maxDailyLoss', parseInt(e.target.value))}
              min="0"
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.5rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        alignItems: 'center',
        marginTop: '2rem',
        paddingTop: '1rem',
        borderTop: '1px solid var(--sacred-border)'
      }}>
        <button 
          className="action-btn primary"
          onClick={saveSettings}
          style={{ fontSize: '0.7rem', padding: '0.5rem 1.5rem' }}
        >
          SAVE SETTINGS
        </button>
        
        {saveStatus && (
          <span style={{ 
            fontSize: '0.65rem',
            color: saveStatus.includes('success') ? 'var(--sacred-green)' : 'var(--sacred-red)'
          }}>
            {saveStatus}
          </span>
        )}
      </div>
    </div>
  )
}